import React, { useState } from 'react';
import { Mail, Trash2, Plus, Send, Info } from 'lucide-react';
import {
  AuthHeading,
  AuthField,
  AuthInput,
  AuthSelect,
  AuthTextarea,
  PrimaryButton,
  ErrorBanner,
} from '../../components/auth/AuthPrimitives';
import organizationsApi from '../../utils/organizationsApi';

// Invite roles mirror OrganizationMembership.ROLE_CHOICES minus `owner`
// (owner is assigned at org creation, never via invitation).
const INVITE_ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Manage members, billing, and settings.' },
  { value: 'provider', label: 'Provider', desc: 'Generate and send appeals.' },
  { value: 'biller', label: 'Biller', desc: 'Upload denials, manage claims.' },
  { value: 'auditor', label: 'Auditor', desc: 'Read-only access to appeals and outcomes.' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const newRow = () => ({ email: '', role: 'biller' });

/**
 * Owner/admin invitation composer, faithful to the prototype InviteSendScreen.
 *
 * Multiple {email, role} rows, an optional note, and a Send action that calls
 * createInvitation once per row. The backend dispatches the raw code
 * out-of-band; the frontend NEVER renders a persisted token — it only shows a
 * sent confirmation listing each invitee as "Invited".
 */
function InviteSendPanel({ organizationId, orgName }) {
  const [rows, setRows] = useState([newRow()]);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs));

  async function handleSend(ev) {
    ev?.preventDefault();
    setSubmitError('');
    const nextErrors = {};
    rows.forEach((r, i) => {
      if (!r.email.trim()) nextErrors[i] = 'Enter an email';
      else if (!EMAIL_RE.test(r.email.trim())) nextErrors[i] = 'Invalid email';
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    try {
      await Promise.all(
        rows.map((r) =>
          organizationsApi.createInvitation({
            invited_email: r.email.trim(),
            role: r.role,
            organizationId,
          }),
        ),
      );
      setSent(rows.map((r) => ({ email: r.email.trim(), role: r.role })));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.invited_email;
      setSubmitError(
        Array.isArray(detail) ? detail.join(' ') : detail || 'Could not send the invitations. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }

  function inviteMore() {
    setSent(null);
    setRows([newRow()]);
    setNote('');
    setErrors({});
    setSubmitError('');
  }

  if (sent) {
    return (
      <div style={{ maxWidth: 480 }}>
        <div
          style={{
            width: 56,
            height: 56,
            marginBottom: 24,
            background: '#000',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Send size={24} style={{ color: '#fff' }} />
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#000' }}>
          {sent.length > 1 ? `${sent.length} invitations sent` : 'Invitation sent'}
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.55, color: '#64748B', margin: '12px 0 0' }}>
          We emailed a join code{orgName ? ` for ${orgName}` : ''}. They&rsquo;ll show as <em>Invited</em> until they
          accept.
        </p>
        <div style={{ margin: '24px 0 0', border: '1px solid #000' }}>
          {sent.map((r, i) => (
            <div
              key={r.email}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 16px',
                borderTop: i ? '0.4px solid #D5DAE1' : 'none',
              }}
            >
              <span style={{ fontSize: 14, color: '#000', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.email}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 12, color: '#64748B', textTransform: 'capitalize' }}>{r.role}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#B7791F',
                    border: '1px solid #B7791F',
                    padding: '2px 8px',
                  }}
                >
                  Invited
                </span>
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 24 }}>
          <PrimaryButton full={false} icon={Plus} onClick={inviteMore}>
            Invite more
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSend} noValidate style={{ maxWidth: 560 }}>
      <AuthHeading
        eyebrow={orgName ? `Organization · ${orgName}` : 'Organization'}
        title="Invite your team"
        subtitle="Send an invitation with a join code. Each person joins with the role you assign — you can change it later."
      />
      <ErrorBanner>{submitError}</ErrorBanner>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 150px auto', gap: 8, alignItems: 'start' }}>
            <AuthField htmlFor={`inv-email-${i}`} error={errors[i]}>
              <AuthInput
                id={`inv-email-${i}`}
                type="email"
                placeholder="colleague@clinic.com"
                leadingIcon={Mail}
                value={r.email}
                onChange={(e) => setRow(i, 'email', e.target.value)}
                error={errors[i]}
              />
            </AuthField>
            <AuthSelect id={`inv-role-${i}`} value={r.role} onChange={(e) => setRow(i, 'role', e.target.value)}>
              {INVITE_ROLES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </AuthSelect>
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={rows.length === 1}
              aria-label="Remove recipient"
              style={{
                height: 47,
                width: 40,
                border: '1px solid #D5DAE1',
                background: '#fff',
                cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                color: rows.length === 1 ? '#C5CBD3' : '#64748B',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        style={{
          marginTop: 12,
          background: 'none',
          border: 'none',
          padding: '4px 0',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: '#000',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontFamily: 'inherit',
        }}
      >
        <Plus size={15} /> Add another
      </button>

      <div style={{ marginTop: 20 }}>
        <AuthField label="Add a note" htmlFor="inv-note" optional>
          <AuthTextarea
            id="inv-note"
            rows={3}
            placeholder="Hey — joining our Meduri workspace so we can start appealing denials together."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </AuthField>
      </div>

      <div style={{ marginTop: 24 }}>
        <PrimaryButton type="submit" loading={loading} icon={Send}>
          Send {rows.length > 1 ? `${rows.length} Invitations` : 'Invitation'}
        </PrimaryButton>
      </div>
      <p style={{ fontSize: 12, color: '#64748B', margin: '14px 0 0', lineHeight: 1.5, display: 'flex', gap: 6 }}>
        <span style={{ flexShrink: 0, marginTop: 1 }}>
          <Info size={13} />
        </span>
        Invites expire in 7 days. Recipients confirm the join before appearing as active members.
      </p>
    </form>
  );
}

export default InviteSendPanel;
